import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import { Option } from '@repo/toolkit/types'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { PharmacyIcon } from '@repo/ui/icons'
import Lock from '@repo/ui/lock'
import Typography from '@repo/ui/typography'
import { TFunction } from 'i18next'
import React, { ReactElement } from 'react'
import { Link } from 'react-router-dom'

import { staffPositionEnumMapping } from '../../../lib/types/enums/staff/staff-position.enum'
import { TripTypeEnum } from '../../../lib/types/enums/trip/trip-type.enum'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { TripDateProperties } from '../../../lib/types/models/trip-booking/properties.trip-date'
import { TripBookingAllocationToCenterRequest } from '../../../lib/types/models/trip-booking/trip-booking-allocation-to-center-request'
import {
  TripBookingDispatchPlaceIndividualTypeEnum,
  TripDate,
  TripDateLight,
  TripDateTripManagerMapping
} from '../../../lib/types/models/trip-booking/trip-date'
import { AllocatedPlaceInput } from '../../../lib/types/models/trip-booking/trip-date-input'
import { TripBookingDispatchPlace } from '../../../lib/types/models/trip-booking-dispatch-places/trip-booking-dispatch-place'
import { TripBookingDispatchPlaceProperties } from '../../../lib/types/models/trip-booking-dispatch-places/trip-booking-dispatch-place.properties'
import { TripPlaceToCenterRequest } from '../../../lib/types/models/trip-place-to-center-request/trip-place-to-center-request'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { constructTripFolderPath } from '../../../views/trip-folder/utils'
import { QualificationTags } from '../../elements/qualification-badge'
import { StaffBadge } from '../staff/staff.ui'
import { TripBookingDetailsBadge } from '../trip-bookings/trip-booking.ui'
import { AllocatedPlaceFormInput } from './trip-date-booking-to-center-form'
import {
  FriendBadge,
  JourneyBadge,
  NumberOfPlacesBadge,
  TripDateDirectionalityBadge,
  TripDateEcartBadge
} from './trip-dates.ui'

type RemainingPlacesType = {
  remainingStudentPlaces: number
  remainingStaffPlaces: number
}

export function allocatedPlacesInputMapping(
  allocatedPlaces: TripBookingAllocationToCenterRequest[]
): AllocatedPlaceFormInput {
  const data = allocatedPlaces.map(
    ({ id: allocatedPlaceId, numberOfStudentsPlacesAllocated, numberOfStaffPlacesAllocated }) => ({
      allocatedPlaceId,
      numberOfStudentsPlacesAllocated,
      numberOfStaffPlacesAllocated
    })
  )
  return { data }
}

export const getRemainingPlaces = (
  data: AllocatedPlaceInput[],
  tripPlaceToCenterRequest: TripPlaceToCenterRequest[]
): RemainingPlacesType => {
  if (!data) return { remainingStudentPlaces: 0, remainingStaffPlaces: 0 }

  const totalStudentsPlacesAllocated = data.reduce(
    (acc, curr) => acc + curr.numberOfStudentsPlacesAllocated,
    0
  )
  const totalStudentsPlacesRequested = tripPlaceToCenterRequest.reduce(
    (acc, curr) => acc + curr.numberOfStudentsPlacesRequested,
    0
  )

  const totalStaffPlacesAllocated = data.reduce(
    (acc, curr) => acc + curr.numberOfStaffPlacesAllocated,
    0
  )

  const totalStaffPlacesRequested = tripPlaceToCenterRequest.reduce(
    (acc, curr) => acc + (curr.numberOfStaffPlacesRequested || 0),
    0
  )

  const remainingStudentsPlaces = totalStudentsPlacesRequested - totalStudentsPlacesAllocated
  const remainingStaffPlaces = totalStaffPlacesRequested - totalStaffPlacesAllocated

  return {
    remainingStudentPlaces: remainingStudentsPlaces > 0 ? remainingStudentsPlaces : 0,
    remainingStaffPlaces: remainingStaffPlaces > 0 ? remainingStaffPlaces : 0
  }
}

type TripDatesVersion = 'PRIMARY' | 'SECONDARY' | 'LIGHT'
type ColumnType = TripDate | TripDateLight

type TripDateColumn<T extends ColumnType> = Omit<Column<T>, 'header'> & {
  header: string | ReactElement
  version: TripDatesVersion[]
}

export const CenterLockStatus = ({ locked }: { locked: boolean }) => <Lock isLocked={locked} />

const tripDateColumns: <T extends ColumnType>() => TripDateColumn<T>[] = () => [
  {
    version: ['PRIMARY', 'SECONDARY', 'LIGHT'],
    accessorKey: 'date',
    header: 'Date',
    enableSorting: true,
    cell: ({ row }) => <Badge text={DateFormatter.DDMMYYYY(row.original.date)} />
  },
  {
    version: ['PRIMARY', 'SECONDARY', 'LIGHT'],
    accessorKey: 'directionality',
    header: 'Type de trajet',
    enableSorting: true,
    cell: ({ row }) => <TripDateDirectionalityBadge directionality={row.original.directionality} />
  },
  {
    version: ['PRIMARY', 'SECONDARY', 'LIGHT'],
    accessorKey: 'trip.departure.label',
    header: 'Voyage',
    enableSorting: true,
    cell: ({ row }) => TripProperties.getTripLabel(row.original.trip)
  },
  {
    version: ['LIGHT'],
    accessorKey: 'inboundTripPlaceToCenterRequests.center.name',
    header: 'Centres',
    cell: ({ row }) => TripDateProperties.getCenters(row.original)
  },
  {
    version: ['LIGHT'],
    accessorKey: 'numberOfStudentsPlacesRequested',
    header: 'Places à répartir',
    cell: ({ row }) => (
      <NumberOfPlacesBadge
        className={'py-2'}
        studentsPlaces={row.original.numberOfStudentsPlacesRequested}
        staffPlaces={row.original.numberOfStaffPlacesRequested}
      />
    )
  },
  {
    version: ['LIGHT'],
    accessorKey: 'numberOfStudentsPlacesAllocated',
    header: '# Places réparties',
    cell: ({ row }) => (
      <NumberOfPlacesBadge
        className={'py-2'}
        studentsPlaces={row.original.numberOfStudentsPlacesAllocated}
        staffPlaces={row.original.numberOfStaffPlacesAllocated}
      />
    )
  },
  {
    version: ['PRIMARY', 'SECONDARY'],
    accessorKey: 'numberOfStudentsPlacesReserved',
    header: '# Places réservées',
    cell: ({ row }) => {
      const tripDate = row.original as TripDate
      return (
        <NumberOfPlacesBadge
          className={'py-2'}
          studentsPlaces={tripDate.numberOfStudentsPlacesReserved}
          staffPlaces={tripDate.numberOfStaffPlacesReserved}
        />
      )
    }
  },
  {
    version: ['PRIMARY'],
    accessorKey: 'numberOfStudentsPlacesRequested',
    header: '# Places demandées',
    cell: ({ row }) => {
      const tripDate = row.original as TripDate
      return (
        <NumberOfPlacesBadge
          className={'py-2'}
          studentsPlaces={tripDate.numberOfStudentsPlacesRequested}
          staffPlaces={tripDate.numberOfStaffPlacesRequested}
        />
      )
    }
  },
  {
    version: ['SECONDARY'],
    accessorKey: 'numberOfStudentsPlacesDispatched',
    header: '# Places dispatchées',
    cell: ({ row }) => {
      const tripDate = row.original as TripDate
      return (
        <NumberOfPlacesBadge
          className={'py-2'}
          studentsPlaces={tripDate.numberOfStudentsPlacesDispatched}
          staffPlaces={tripDate.numberOfStaffPlacesDispatched}
        />
      )
    }
  },
  {
    version: ['PRIMARY', 'SECONDARY'],
    accessorKey: 'numberOfRemainingStudentPlaces',
    header: '# Ecart',
    cell: ({ row }) => <TripDateEcartBadge tripDate={row.original as TripDate} />
  },
  {
    version: ['PRIMARY'],
    accessorKey: 'numberOfStudentsPlacesDispatched',
    header: '# Places dispatchées',
    cell: ({ row }) => {
      const tripDate = row.original as TripDate
      return (
        <NumberOfPlacesBadge
          className={'py-2'}
          studentsPlaces={tripDate.numberOfStudentsPlacesDispatched}
          staffPlaces={tripDate.numberOfStaffPlacesDispatched}
        />
      )
    }
  },
  {
    version: ['PRIMARY', 'SECONDARY'],
    accessorKey: 'dispatchedCenters',
    header: 'Centre(s)',
    cell: ({ row }) => {
      const tripDate = row.original as TripDate
      const centers = tripDate.dispatchedCenters ?? []

      if (centers.length === 0) return '-'

      return (
        <div className="flex flex-col gap-2 py-2 min-w-40">
          {centers.map((center) => (
            <div key={center.id} className="flex items-center gap-2">
              <Typography variant="label-medium">{center.name}</Typography>
              <CenterLockStatus locked={center.locked} />
            </div>
          ))}
        </div>
      )
    }
  },
  {
    version: ['PRIMARY', 'SECONDARY'],
    accessorKey: 'tripPlaceToCenterRequests',
    header: 'Réservations',
    cell: ({ row }) => {
      const tripDate = row.original as TripDate
      const tripBookings = tripDate.tripBookings

      return (
        <div className="flex flex-col h-full gap-3 border-l border-r border-grey-light py-4 box-border">
          {tripBookings.map((tripBooking, index) => {
            const { tripFolders } = tripBooking
            const tripFolder = tripFolders?.find(
              (folder) => folder.directionality === tripDate.directionality
            )
            return (
              <React.Fragment key={tripBooking.id}>
                <div className={'px-3 flex items-center'}>
                  <div className="max-w-[400px] w-[400px]">
                    <TripBookingDetailsBadge
                      key={tripBooking.id}
                      directionality={row.original.directionality}
                      tripBooking={tripBooking}
                      displayViewButton
                      detailOptions={{
                        count: ['reserved-dispatched'],
                        showCost: false,
                        displayArrivalTime: true
                      }}
                      isActive={!!tripFolder}
                    />
                  </div>

                  <div className="flex flex-col gap-2 ml-6 mr-2">
                    {!!tripFolder ? (
                      <div className={'px-3'}>
                        <Link
                          to={constructTripFolderPath(tripFolder.id)}
                          className="underline text-blue"
                          target="_blank"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Voir le dossier voyage
                        </Link>
                      </div>
                    ) : (
                      <div className={'px-3'}>
                        <Typography variant="label-semibold">-</Typography>
                      </div>
                    )}
                  </div>
                </div>

                {index !== tripBookings.length - 1 && (
                  <div className={'h-px w-full bg-grey-light'} />
                )}
              </React.Fragment>
            )
          })}
        </div>
      )
    }
  }
]

export const getTripDateColumns = <T extends ColumnType>(
  version: TripDatesVersion
): Column<T>[] => {
  const columns = tripDateColumns<T>()
  return columns.filter((column) => column.version.includes(version)) as Column<T>[]
}

type DispatchColumn<T> = Column<T> & {
  version: TripDateVariantType[]
}

const enrollmentsDispatchColumns = (): DispatchColumn<TripBookingDispatchPlace>[] => [
  {
    version: ['PRIMARY', 'SECONDARY'],
    accessorKey: 'childLastName',
    header: 'NOM',
    enableSorting: true,
    sortingFn: 'text',
    cell: ({ row }) => row.original.enrollment?.childLastName?.toUpperCase() ?? ''
  },
  {
    version: ['PRIMARY', 'SECONDARY'],
    accessorKey: 'childFirstName',
    header: 'Prénom',
    cell: ({ row }) =>
      row.original.enrollment?.childFirstName
        ? StringFormatter.capitalize(row.original.enrollment.childFirstName)
        : undefined
  },
  {
    version: ['PRIMARY', 'SECONDARY'],
    accessorKey: 'declaredFriends',
    header: 'Amis',
    cell: ({ row }) => {
      const friends = row.original.enrollment?.declaredFriends
      if (!friends || friends.length === 0) return '-'
      return (
        <div className={'py-2 flex flex-col gap-2'}>
          {friends.map((friend) => (
            <FriendBadge key={friend.id} {...friend} />
          ))}
        </div>
      )
    }
  },
  {
    version: ['SECONDARY'],
    accessorKey: 'journey',
    header: 'Voyage principal',
    cell: ({ row }) =>
      TripProperties.getTripLabel(row.original.enrollment?.mainProposition?.mainTrip)
  },
  {
    version: ['PRIMARY', 'SECONDARY'],
    accessorKey: 'journey.reference',
    header: 'Séjour',
    enableSorting: true,
    sortingFn: 'text',
    cell: ({ row }) => {
      const journey = row.original.enrollment?.mainProposition?.journey
      if (!journey) return undefined
      return <JourneyBadge journey={journey} className={'my-2'} />
    }
  },
  {
    version: ['PRIMARY', 'SECONDARY'],
    accessorKey: 'mainProposition.mainTrip.departure.label',
    header: 'Ville de départ',
    enableSorting: true,
    cell: ({ row }) => {
      const mainProposition = row.original.enrollment?.mainProposition
      const trip = mainProposition?.secondaryTrip ?? mainProposition?.mainTrip
      return TripProperties.getDepartureOrJourneyPlaceLabel(trip)
    }
  },
  {
    version: ['PRIMARY', 'SECONDARY'],
    accessorKey: 'enrollment.hasMedicalConditions',
    header: 'Info santé',
    enableSorting: true,
    cell: ({ row }) => {
      if (row.original.enrollment?.hasMedicalCondition)
        return (
          <PharmacyIcon
            className="!size-5 shrink-0"
            color={tailwindColors.green.DEFAULT}
            color2={tailwindColors.green.light.DEFAULT}
          />
        )
    }
  },
  {
    version: ['PRIMARY', 'SECONDARY'],
    accessorKey: 'enrollmentCenter.center.name',
    header: 'Centre',
    enableSorting: true,
    cell: ({ row }) => row.original.enrollment?.enrollmentCenter?.center.name
  }
]

const staffsDispatchColumns = (
  t: TFunction<'en' | 'fr', undefined>,
  tripManagers?: TripDateTripManagerMapping
): DispatchColumn<TripBookingDispatchPlace>[] => [
  {
    version: ['PRIMARY'],
    accessorKey: 'lastName',
    header: 'NOM',
    cell: ({ row }) => {
      if (row.original.individualType === TripBookingDispatchPlaceIndividualTypeEnum.ENROLLMENT)
        return '-'

      const hasResponsibility =
        !tripManagers ||
        row.original.individualType !== TripBookingDispatchPlaceIndividualTypeEnum.AFFECTED_STAFF
          ? false
          : TripBookingDispatchPlaceProperties.hasResponsibility(row.original, tripManagers)

      return (
        <StaffBadge
          staff={TripBookingDispatchPlaceProperties.getStaffInformation(row.original)}
          hasResponsibility={hasResponsibility}
        />
      )
    }
  },
  {
    version: ['SECONDARY'],
    accessorKey: 'lastName',
    header: 'NOM',
    cell: ({ row }) => {
      if (row.original.individualType === TripBookingDispatchPlaceIndividualTypeEnum.ENROLLMENT)
        return '-'

      const hasResponsibility =
        !tripManagers ||
        row.original.individualType !== TripBookingDispatchPlaceIndividualTypeEnum.AFFECTED_STAFF
          ? false
          : TripBookingDispatchPlaceProperties.hasResponsibility(row.original, tripManagers)

      if (!hasResponsibility)
        return TripBookingDispatchPlaceProperties.getStaffInformation(
          row.original
        ).lastName.toUpperCase()

      return (
        <StaffBadge
          staff={TripBookingDispatchPlaceProperties.getStaffInformation(row.original)}
          hasResponsibility={hasResponsibility}
        />
      )
    }
  },
  {
    version: ['PRIMARY', 'SECONDARY'],
    accessorKey: 'firstName',
    header: 'Prénom',
    cell: ({ row }) => {
      if (row.original.individualType === TripBookingDispatchPlaceIndividualTypeEnum.ENROLLMENT)
        return '-'

      return StringFormatter.capitalize(
        TripBookingDispatchPlaceProperties.getStaffInformation(row.original).firstName
      )
    }
  },
  {
    version: ['PRIMARY', 'SECONDARY'],
    accessorKey: 'position',
    header: 'Poste',
    cell: ({ row }) => {
      if (row.original.individualType === TripBookingDispatchPlaceIndividualTypeEnum.ENROLLMENT)
        return '-'

      return t(
        staffPositionEnumMapping[
          TripBookingDispatchPlaceProperties.getStaffInformation(row.original).position
        ]
      )
    }
  },
  {
    version: ['PRIMARY', 'SECONDARY'],
    accessorKey: 'qualifications',
    header: 'Qualifications',
    cell: ({ row }) => {
      if (row.original.individualType === TripBookingDispatchPlaceIndividualTypeEnum.ENROLLMENT)
        return '-'

      return (
        <QualificationTags
          qualifications={
            TripBookingDispatchPlaceProperties.getStaffInformation(row.original).qualifications
          }
        />
      )
    }
  },
  {
    version: ['PRIMARY', 'SECONDARY'],
    accessorKey: '',
    header: 'Ville de départ',
    cell: ({ row }) => {
      if (row.original.individualType === TripBookingDispatchPlaceIndividualTypeEnum.ENROLLMENT)
        return '-'
      return valueRender(
        getTranslatedName(
          TripBookingDispatchPlaceProperties.getStaffDeparturePlace(row.original)
        ) || undefined
      )
    }
  },
  {
    version: ['PRIMARY'],
    accessorKey: '',
    header: 'Centre',
    cell: ({ row }) => {
      if (
        row.original.individualType !== TripBookingDispatchPlaceIndividualTypeEnum.AFFECTED_STAFF
      )
        return '-'

      return valueRender(
        TripBookingDispatchPlaceProperties.getStaffCenter(row.original),
        (centers) => centers.map((c) => c.name).join(', ')
      )
    }
  },
  {
    version: ['SECONDARY'],
    accessorKey: '',
    header: 'Mission',
    cell: ({ row }) => {
      if (row.original.individualType === TripBookingDispatchPlaceIndividualTypeEnum.ENROLLMENT)
        return '-'

      return valueRender(
        TripBookingDispatchPlaceProperties.getStaffMissions(row.original),
        (missions) => {
          if (Array.isArray(missions)) return missions.map((m) => m.title).join(', ')
          return missions.title
        }
      )
    }
  }
]

export const getEnrollmentsDispatchColumns = (
  version: TripDateVariantType
): Column<TripBookingDispatchPlace>[] => {
  return enrollmentsDispatchColumns().filter((column) => column.version.includes(version))
}

export const getStaffsDispatchColumns = (
  version: TripDateVariantType,
  t: TFunction<'en' | 'fr', undefined>,
  tripManagers?: TripDateTripManagerMapping
): Column<TripBookingDispatchPlace>[] => {
  return staffsDispatchColumns(t, tripManagers).filter((column) =>
    column.version.includes(version)
  )
}

/**
 * get the label for the trip gap. A trip gap is when we have a difference between the number of places reserved and the number of places requested.
 */
export const tripGapLabel = (value: boolean): string => {
  if (value) return 'A un écart'
  return "N'a pas d'écart"
}

export const tripGapOptions: Option<boolean>[] = [
  { label: tripGapLabel(true), value: true },
  { label: tripGapLabel(false), value: false }
]

export const tripDateTitleMapping: Record<TripTypeEnum, string> = {
  [TripTypeEnum.PRIMARY]: 'Voyages principaux réservés',
  [TripTypeEnum.SECONDARY]: 'Voyages secondaires réservés'
}

export type TripDateVariantType = 'PRIMARY' | 'SECONDARY'

type TripDateVariantMappingType = {
  title: string
  tableTitle: string
  type: TripTypeEnum
}

export const tripDateVariantMapping: Record<TripDateVariantType, TripDateVariantMappingType> = {
  PRIMARY: {
    type: TripTypeEnum.PRIMARY,
    title: 'Voyages principaux réservés',
    tableTitle: 'Tous les voyages principaux réservés'
  },
  SECONDARY: {
    type: TripTypeEnum.SECONDARY,
    title: 'Voyages secondaires réservés',
    tableTitle: 'Tous les voyages secondaires réservés'
  }
}
